import React from "react";
import { useLazyQuery } from "@apollo/client";
import TokenizedLinksService from "@app/service/tokenizedLinks";
import { useQuery } from "@app/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { SecurityContext } from "@natera/platform/lib/provider/security";
import { routes } from "@app/routing";
import {
  PrimaryAction,
  TestCard,
  TestCardStatus,
  TestType,
} from "@app/provider/testData/types";
import {
  Language,
  OrderRenewalContext,
  TestCardUtilsContext,
  UserContext,
  ViewResultContext,
} from "@app/provider";
import { STAGE } from "@app/service/user";
import { HEAP_EVENTS, TestDetailsRecord } from "@app/provider/types";
import TestCardService from "@app/service/testCard";
import { UrlVideoType } from "@app/pages/private/simpleOrder/aboutYourTest/aboutYourTest";
import { NotificationTemplateName } from "./useGeneratedTokenizedLink";
import { UPSAPIContext, UPSContext } from "@app/provider";
import { useUPSHeapEvent } from "@app/ups/hooks";

export enum TokenizedLinkAction {
  TOKENIZED_LINK_SIGN_UP = "TOKENIZED_LINK_SIGN_UP",
  ACTIVATE_USER = "ACTIVATE_USER",
  RESET_PASSWORD = "RESET_PASSWORD",
  UNLOCK_USER = "UNLOCK_USER",
  UPDATE_EMAIL = "UPDATE_EMAIL",
  OPEN_NEW_ORDER = "OPEN_NEW_ORDER",
  OPEN_TEST_RESULT = "OPEN_TEST_RESULT",
  SCHEDULE_DRAW = "SCHEDULE_DRAW",
  SIMPLE_ORDER_COMPLETION = "SIMPLE_ORDER_COMPLETION",
  UPP_PREBILL_INSURANCE_GUEST_LOGIN = "UPP_PREBILL_INSURANCE_GUEST_LOGIN",
  UPP_POSTBILL_GUEST_LOGIN = "UPP_POSTBILL_GUEST_LOGIN",
  OPEN_RENEWAL_PAGE = "OPEN_RENEWAL_PAGE",
  UPP_UPS_TOKENIZED_LINK_SIGN_UP = "UPP_UPS_TOKENIZED_LINK_SIGN_UP",
}

export enum LinkSource {
  SMS = "sms",
  EMAIL = "email",
  // No delivery source (e.g. the public MP order-match token). Like SMS it isn't
  // EMAIL, so the sign-up flow can't skip OTP email verification.
  NONE = "none",
}

export type TokenizedLinkData =
  | ActivateUserData
  | TestData
  | ResetPasswordData
  | UpdateEmailData;

export type ActivateUserData = {
  email: string;
};

export type BillingCenterData = {
  orderUidPublic: string;
  lineItemUid: string;
};

export type UPSLinkData = {
  tokenizedLinkType: string;
  patientUid: string;
  paymentRequestId?: string;
  paymentPlanId?: string;
  receiptId?: string;
};

export type TestData = {
  orderUidPublic: string;
  testUid: string;
  testType: TestType;
};

export type ResetPasswordData = {
  email: string;
  unlock: boolean | undefined;
};

export type UpdateEmailData = {
  email: string;
  nextEmail: string;
};

export type TokenPayload =
  | ActivateUser
  | ResetPassword
  | UpdateEmail
  | OpenOrder
  | OpenTestResult
  | ScheduleDraw
  | SimpleOrderCompletion
  | BillingCenter
  | UPSLinkSignIn
  | OpenRenewalPage;

export interface LinkSourceData {
  source: LinkSource;
  language?: Language;
  notificationType?: NotificationTemplateName;
}

export interface LinkUserData {
  email: string;
}

export type BillingCenter = {
  action: TokenizedLinkAction.UPP_POSTBILL_GUEST_LOGIN;
  data: BillingCenterData;
  sourceData: LinkSourceData;
};

export type UPSLinkSignIn = {
  action: TokenizedLinkAction.UPP_UPS_TOKENIZED_LINK_SIGN_UP;
  data: UPSLinkData;
  sourceData: LinkSourceData;
};

export type ActivateUser = {
  action: TokenizedLinkAction.ACTIVATE_USER;
  data: ActivateUserData;
  sourceData: LinkSourceData;
};

export type ResetPassword = {
  action: TokenizedLinkAction.RESET_PASSWORD;
  data: ResetPasswordData;
  sourceData: LinkSourceData;
};

export type UnlockUser = {
  action: TokenizedLinkAction.UNLOCK_USER;
  data: ResetPasswordData;
  sourceData: LinkSourceData;
};

export type UpdateEmail = {
  action: TokenizedLinkAction.UPDATE_EMAIL;
  data: UpdateEmailData;
  sourceData: LinkSourceData;
};

export type OpenOrder = {
  action: TokenizedLinkAction.OPEN_NEW_ORDER;
  data: TestData;
  sourceData: LinkSourceData;
};

export type OpenTestResult = {
  action: TokenizedLinkAction.OPEN_TEST_RESULT;
  data: TestData;
  sourceData: LinkSourceData;
};

export type OpenRenewalPage = {
  action: TokenizedLinkAction.OPEN_RENEWAL_PAGE;
  data: TestData;
  sourceData: LinkSourceData;
};

export type ScheduleDraw = {
  action: TokenizedLinkAction.SCHEDULE_DRAW;
  data: TestData;
  sourceData: LinkSourceData;
};

export type SimpleOrderCompletion = {
  action: TokenizedLinkAction.SIMPLE_ORDER_COMPLETION;
  data: TestData;
  sourceData: LinkSourceData;
};

export type GetOrderUidPublicFromTokenPayload = {
  action: TokenizedLinkAction;
  orderUidPublic: string;
  sourceData: LinkSourceData;
};

export type GetPatientUidFromToken = {
  patientUid: string;
};

type TokenizedLinksController = {
  isUserExistData?: boolean;
  checkUserExistByToken: (token: string) => Promise<boolean>;
  isLoading: boolean;
  getTokenSource: (
    token: string,
    intentToken?: string
  ) => Promise<LinkSourceData | undefined>;
  getOrderUidPublic: (
    token: string
  ) => Promise<GetOrderUidPublicFromTokenPayload | undefined>;
  getPatientUidFromTokenHandler: (
    token: string
  ) => Promise<GetPatientUidFromToken | undefined>;
  getUserEmailByToken: (
    token: string,
    intentToken?: string
  ) => Promise<string | undefined>;
  getTokenLanguage: (token: string) => Promise<Language | undefined>;
};

type UseTokenizedLink = () => TokenizedLinksController;

const useTokenizedLink: UseTokenizedLink = () => {
  const notRedirectedLinks = [
    routes.changePassword,
    routes.setPassword,
    routes.updateEmail,
    routes.activation,
    routes.scheduleBloodDraw,
    routes.guestLogin,
    routes.postbillGuestLogin,
    routes.drawRequest,
    routes.outOfNetwork,
    routes.priorAuthorization,
    routes.nonCoveredService,
    routes.billingEstimate,
    routes.noLongerAvailable,
    routes.insuranceNoLongerAvailable,
    routes.loginRedirect,
    routes.upsGuestLogin,
  ];
  const query = useQuery();
  const navigate = useNavigate();
  const location = useLocation();

  const token = query.get("token");
  const guest_token = query.get("guest_token"); //UPS guest token
  const { isAuthorized } = React.useContext(SecurityContext);
  const { getTestCardData } = React.useContext(TestCardUtilsContext);
  const { uppUser } = React.useContext(UserContext);
  const { redirectionMap } = React.useContext(UPSContext);
  const { loginValidation } = React.useContext(UPSAPIContext);
  const { trackUPSHeapEvent } = useUPSHeapEvent();
  const { openResultPage, openTestResultDetailsPage } =
    React.useContext(ViewResultContext);

  const { openRenewalDialog } = React.useContext(OrderRenewalContext);

  const [isLoading, setLoading] = React.useState<boolean>(false);

  const isAuth = isAuthorized();
  const [isUserExist, { data: isUserExistData }] = useLazyQuery<{
    checkUserExistByToken: { isExist: boolean };
  }>(TokenizedLinksService.checkUserExistsByToken());

  const [getResourceFromToken] = useLazyQuery<{
    getResourceFromToken: TokenPayload;
  }>(TokenizedLinksService.getResourceFromToken());

  const [getResourceFromUPSToken] = useLazyQuery<{
    getResourceFromUPSToken: UPSLinkSignIn;
  }>(TokenizedLinksService.getResourceFromUPSToken());

  const [getTokenSource] = useLazyQuery<{
    getTokenSource: LinkSourceData;
  }>(TokenizedLinksService.getTokenSource());

  const [getTokenLanguage] = useLazyQuery<{
    getLanguageByToken: { language?: Language };
  }>(TokenizedLinksService.getLanguageByToken());

  const [getUserEmailByToken] = useLazyQuery<{
    getUserEmailByToken: LinkUserData;
  }>(TokenizedLinksService.getUserEmailByToken());

  const [getTestCardDetails] = useLazyQuery<{
    getTestCardDetails: TestDetailsRecord;
  }>(TestCardService.getTestCardDetails(), {
    fetchPolicy: "network-only",
  });

  const [getOrderUidPublicFromToken] = useLazyQuery<{
    getOrderUidPublicFromToken: GetOrderUidPublicFromTokenPayload;
  }>(TokenizedLinksService.getOrderUidPublicFromToken());

  const [getPatientUidFromToken] = useLazyQuery<{
    getPatientUidFromToken: GetPatientUidFromToken;
  }>(TokenizedLinksService.getPatientUidFromToken());

  const checkUserExistByToken = async (tokenFromLink: string) => {
    const response = await isUserExist({ variables: { token: tokenFromLink } });
    return Boolean(response.data?.checkUserExistByToken?.isExist);
  };

  const handleUPSLogin = async () => {
    try {
      const response = await loginValidation({
        guest_token: (guest_token ?? token) as string,
      });
      navigate(
        redirectionMap[response?.data?.link_type as string] || `${routes.home}`
      );
    } catch (error) {
      console.log(error);
      navigate(`${routes.home}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGetTestDetails = async (
    orderUidPublic: string,
    testUid: string
  ) => {
    const response = await getTestCardDetails({
      variables: { orderUidPublic, testUid },
    });

    const testDetailsData = response.data?.getTestCardDetails;

    return getTestCardData({ ...testDetailsData } as TestCard);
  };

  const openResult = async (payload: OpenTestResult) => {
    const test = await handleGetTestDetails(
      payload.data.orderUidPublic,
      payload.data.testUid
    );
    setLoading(false);
    if (test.status === TestCardStatus.RESULT_READY) {
      openResultPage({ ...test });
    } else {
      openTestResultDetailsPage({
        ...test,
      });
    }
  };

  const openScheduleDraw = async (payload: ScheduleDraw) => {
    const test = await handleGetTestDetails(
      payload.data.orderUidPublic,
      payload.data.testUid
    );

    if (test.primaryAction === PrimaryAction.SCHEDULE_A_DRAW) {
      navigate(
        routes.sampleDrawPage(
          payload.data.orderUidPublic,
          payload.data.testUid
        ),
        {
          state: {
            fromTokenizedLink: true,
          },
          replace: true,
        }
      );
    } else {
      navigate(routes.home, { replace: true });
    }
  };

  const openSimpleOrderCompletionFlow = async (
    payload: SimpleOrderCompletion
  ) => {
    const test = await handleGetTestDetails(
      payload.data.orderUidPublic,
      payload.data.testUid
    );
    if (test.status === TestCardStatus.TEST_ORDERED) {
      const urlVideoType = test.isComboOrder
        ? UrlVideoType.COMBO_ORDER
        : test.testType;
      navigate(
        routes.aboutTestPage(payload.data.orderUidPublic, urlVideoType),
        {
          replace: true,
        }
      );
    } else {
      navigate(routes.home, { replace: true });
    }
  };

  const getResource = async (tokenFromLink: string) => {
    setLoading(true);
    const response = await getResourceFromToken({
      variables: { token: tokenFromLink },
    });
    const payload = response.data?.getResourceFromToken;
    if (!payload) {
      navigate(routes.home, { replace: true });
      return;
    }

    switch (payload.action) {
      case TokenizedLinkAction.OPEN_TEST_RESULT:
        openResult(payload);
        break;
      case TokenizedLinkAction.SCHEDULE_DRAW:
        setLoading(false);
        openScheduleDraw(payload);
        break;
      case TokenizedLinkAction.SIMPLE_ORDER_COMPLETION:
        setLoading(false);
        openSimpleOrderCompletionFlow(payload);
        break;
      case TokenizedLinkAction.OPEN_RENEWAL_PAGE:
        setLoading(false);
        openRenewalDialog();
        break;
      case TokenizedLinkAction.UPP_POSTBILL_GUEST_LOGIN:
        setLoading(false);
        navigate(routes.billingCenter);
        break;
      case TokenizedLinkAction.UPP_UPS_TOKENIZED_LINK_SIGN_UP:
        handleUPSLogin();
        break;
      case TokenizedLinkAction.OPEN_NEW_ORDER:
      default:
        setLoading(false);
        navigate(routes.home, { replace: true });
    }
  };

  const getTokenSourceHandler = async (token: string, intentToken?: string) => {
    const response = await getTokenSource({
      variables: { token, intentToken },
    });

    // useLazyQuery puts GraphQL errors on `response.error`; rethrow so a rejected
    // proof can route the caller back to DOB.
    if (response.error) {
      throw response.error;
    }

    return response.data?.getTokenSource;
  };

  const getTokenLanguageHandler = async (token: string) => {
    const response = await getTokenLanguage({
      variables: { token },
    });

    return response.data?.getLanguageByToken?.language;
  };

  const getUserEmailByTokenHandler = async (
    token: string,
    intentToken?: string
  ) => {
    const response = await getUserEmailByToken({
      variables: { token, intentToken },
    });

    if (response.error) {
      throw response.error;
    }

    return response.data?.getUserEmailByToken.email;
  };

  const handle = async () => {
    if (!token && !guest_token) return;

    const isRedirectedNeeded = !notRedirectedLinks.includes(location.pathname);
    if (!isRedirectedNeeded) return;

    let isUPS = false;
    if (guest_token) {
      const tokenResourceUPS = await getResourceFromUPSToken({
        variables: { token: guest_token },
      });
      isUPS =
        tokenResourceUPS.data?.getResourceFromUPSToken?.action ===
        TokenizedLinkAction.UPP_UPS_TOKENIZED_LINK_SIGN_UP;
    }

    if (!isAuth) {
      const userExist = await checkUserExistByToken(token as string);
      const correctRoute = userExist ? routes.logIn : routes.signUp;
      // UPP-5865: preserve the guest-flow DOB-proof handle (`si`) across the
      // redirect so the sign-up landing can skip the DOB screen.
      const si = query.get("si");
      const siParam = si ? `&si=${si}` : "";
      navigate(`${correctRoute}/?token=${token}${siParam}`, { replace: true });
    } else if (uppUser?.stage === STAGE.FILLED) {
      getResource(isUPS ? (guest_token as string) : (token as string));
    } else if (isUPS) {
      trackUPSHeapEvent({
        eventKey: HEAP_EVENTS.ups_redirect_incompleteaccount,
      });
    }
  };

  const getOrderUidPublic = async (tokenFromLink: string) => {
    setLoading(true);
    const response = await getOrderUidPublicFromToken({
      variables: { token: tokenFromLink },
    });
    const payload = response.data?.getOrderUidPublicFromToken;
    if (!payload) {
      navigate(routes.home, { replace: true });
      return;
    }

    return payload;
  };

  const getPatientUidFromTokenHandler = async (tokenFromLink: string) => {
    setLoading(true);
    const response = await getPatientUidFromToken({
      variables: { token: tokenFromLink },
    });
    const payload = response.data?.getPatientUidFromToken;
    if (!payload) {
      navigate(routes.home, { replace: true });
      return;
    }

    return payload;
  };

  React.useEffect(() => {
    handle();
  }, [isAuth, token, uppUser?.stage, guest_token]);

  return {
    isUserExistData: isUserExistData?.checkUserExistByToken?.isExist,
    checkUserExistByToken,
    isLoading,
    getTokenSource: getTokenSourceHandler,
    getOrderUidPublic,
    getPatientUidFromTokenHandler,
    getUserEmailByToken: getUserEmailByTokenHandler,
    getTokenLanguage: getTokenLanguageHandler,
  };
};

export default useTokenizedLink;
